import { useEffect, useRef } from 'react';
import { howItWorksSteps } from '../../../mocks/landingData';

export default function HowItWorksSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const bannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-slide-up');
          }
        });
      },
      { threshold: 0.1 }
    );

    const section = sectionRef.current;
    if (section) {
      const elements = section.querySelectorAll('.animate-on-scroll');
      elements.forEach((el) => observer.observe(el));
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (bannerRef.current && sectionRef.current) {
        const section = sectionRef.current;
        const rect = section.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const progress = -rect.top / (windowHeight + rect.height);
        const offset = progress * 80;
        bannerRef.current.style.transform = `translateY(${offset}px) scale(1.1)`;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section id="how-it-works" ref={sectionRef} className="relative py-20 md:py-28 overflow-hidden">
      {/* Parallax Banner Background */}
      <div
        ref={bannerRef}
        className="absolute inset-0 w-full h-full transition-transform will-change-transform"
        style={{ transform: 'translateY(0) scale(1.1)' }}
      >
        <img
          src="https://static.readdy.ai/image/ff63a95a3d068db59710e28833339873/2a2918d50033abde4f215a8ef5dbe649.png"
          alt="TNT Coach AI How It Works Banner"
          className="w-full h-full object-cover object-center"
        />
        {/* Dark overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-midnight/80 via-midnight/70 to-midnight/80" />
        <div className="absolute inset-0 bg-gradient-to-r from-midnight/40 via-transparent to-midnight/40" />
      </div>

      {/* Ambient glow */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-champagne-gold/3 blur-3xl pointer-events-none" />

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="animate-on-scroll opacity-0 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-champagne-gold/30 bg-champagne-gold/10 backdrop-blur-sm drop-shadow-md mb-6">
              <i className="ri-settings-5-line text-champagne-gold text-sm" />
              <span className="text-champagne-gold text-sm font-medium">How It Works</span>
            </div>

            <h2 className="animate-on-scroll opacity-0 font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-warm-white drop-shadow-lg mb-6">
              Get coached in{' '}
              <span className="gold-gradient-text">under 60 seconds.</span>
            </h2>
          </div>

          <div className="relative">
            <div className="hidden lg:block absolute top-16 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-royal-navy to-transparent" />
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6 md:gap-4">
              {howItWorksSteps.map((step, index) => (
                <div
                  key={index}
                  className="animate-on-scroll opacity-0 relative text-center"
                >
                  <div className="relative z-10 inline-flex items-center justify-center w-16 h-16 rounded-2xl gold-gradient mb-4 mx-auto shadow-lg shadow-champagne-gold/20">
                    <i className={`${step.icon} text-midnight text-2xl`} />
                  </div>
                  <div className="mb-2">
                    <span className="text-champagne-gold text-xs font-bold tracking-wider drop-shadow-md">STEP {step.step}</span>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-warm-white drop-shadow-md mb-2">{step.title}</h3>
                  <p className="text-warm-white/80 text-sm leading-relaxed drop-shadow-sm">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}